// src/api/registreNcs.api.ts — SCE Issues
// Le registre de la qualité (« NC, DP, Events & Actions.xlsm », site QM_SCE)
// et la recherche du N° d'une NC-S envoyée depuis l'appli (06/10).
// Règles du rapprochement : utils/registreNcs.ts.
//
// ─── Un fichier de 10 Mo : ne le télécharger qu'à bon escient ───
//
// Le registre est retrouvé par son LIEN (`/shares`, pas besoin de l'ID du site
// QM_SCE). Avant tout, une petite requête lit sa version (eTag) :
//   - inchangé depuis la dernière lecture → on reprend les NC-S gardées dans
//     le navigateur (localStorage, ~100 ko : les seules lignes NC-S) ;
//   - modifié → téléchargé et relu (~0,5 s une fois reçu, dans un worker :
//     l'interface ne fige jamais), puis gardé.
// À l'ouverture d'un problème qui attend son N°, seule la petite requête de
// version part (~1 ko) : le rapprochement se refait en local, et le registre
// n'est retéléchargé QUE s'il a changé — au plus une fois par mise à jour de
// la qualité, pour tous les problèmes. « Chercher à nouveau » refait le tout.
//
// Le registre est lu avec le compte de la personne : sans accès au site
// QM_SCE, la recherche automatique se tait (retentée le lendemain) ; la
// recherche manuelle le dit.
//
// Le formulaire déposé est relu dans le dossier « Déclarations NC-S » : c'est
// lui qui dit le NVS, l'initiateur et la date. Gardé aussi (il ne change plus).

import { GraphApiError, graphGet } from './graphClient'
import { partage } from './partage'
import { DOSSIER_NCS } from './declarationNcs.api'
import { nomComplet } from '@/utils/noms'
import { lireRegistreHorsInterface } from './lectureHorsInterface'
import { lireFormulaireDepose, type FormulaireDepose } from '@/utils/ligneRegistreNcs'
import {
  declarationDepuisNom,
  lireDeclarationEnvoyee,
  type DeclarationEnvoyee,
  type LigneRegistre,
} from '@/utils/registreNcs'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''

/** Lien du registre, donné par Guillaume le 06/10. */
export const URL_REGISTRE_NCS =
  "https://sartorius.sharepoint.com/sites/QM_SCE/Autres%20Documents/Assurance%20Qualit%C3%A9%20(AQ)/Enregistrement%20%26%20Plan%20d'actions/NC%2C%20DP%2C%20Events%20%26%20Actions.xlsm"

const CLE_REGISTRE = 'sce-issues:registre-ncs'
const CLE_INACCESSIBLE = 'sce-issues:registre-ncs:inaccessible'
const CLE_DECLARATIONS = 'sce-issues:ncs-declarations'
const VERSION_CACHE = 2

/** Le registre n'est pas lisible avec ce compte (pas d'accès au site QM_SCE). */
export class RegistreInaccessible extends Error {
  constructor() {
    super("Le registre qualité n'est pas accessible avec votre compte (site QM_SCE).")
    this.name = 'RegistreInaccessible'
  }
}

interface ElementFichier {
  eTag?: string
  '@microsoft.graph.downloadUrl'?: string
}

// ─── Mémoire du navigateur (jamais bloquante) ───

function lireMemoire<T>(cle: string): T | null {
  try {
    const brut = localStorage.getItem(cle)
    return brut ? (JSON.parse(brut) as T) : null
  } catch {
    return null
  }
}

function ecrireMemoire(cle: string, valeur: unknown): void {
  try {
    localStorage.setItem(cle, JSON.stringify(valeur))
  } catch {
    // Mémoire pleine ou interdite : on relira le registre la prochaine fois.
  }
}

/** Jour local `AAAA-MM-JJ`. */
export function jourLocal(d: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// ─── Le registre ───

async function elementRegistre(): Promise<ElementFichier> {
  try {
    return await graphGet<ElementFichier>(`/shares/${partage(URL_REGISTRE_NCS)}/driveItem`)
  } catch (err) {
    if (err instanceof GraphApiError && [401, 403, 404].includes(err.status)) throw new RegistreInaccessible()
    throw err
  }
}

interface MemoireRegistre {
  version: number
  eTag: string
  lignes: LigneRegistre[]
}

type ResultatRegistre = { lignes: LigneRegistre[]; eTag: string; nouveau: boolean }

/** Une lecture à la fois : la fiche et la recherche de fond la partagent. */
let lectureEnCours: Promise<ResultatRegistre> | null = null

/**
 * Les NC-S du registre, et s'il vient d'être relu (`nouveau`). Télécharge
 * seulement si sa version a changé depuis la dernière lecture.
 */
export function lignesRegistreNcs(): Promise<ResultatRegistre> {
  lectureEnCours ??= lireRegistre().finally(() => {
    lectureEnCours = null
  })
  return lectureEnCours
}

async function lireRegistre(): Promise<ResultatRegistre> {
  const element = await elementRegistre()
  const eTag = element.eTag ?? ''
  const memoire = lireMemoire<MemoireRegistre>(CLE_REGISTRE)
  if (memoire && memoire.version === VERSION_CACHE && eTag && memoire.eTag === eTag) {
    return { lignes: memoire.lignes, eTag, nouveau: false }
  }
  const url = element['@microsoft.graph.downloadUrl']
  if (!url) throw new RegistreInaccessible()
  const reponse = await fetch(url)
  if (!reponse.ok) throw new GraphApiError(`Téléchargement du registre NC-S (${reponse.status})`, reponse.status)
  const lignes = await lireRegistreHorsInterface(await reponse.arrayBuffer())
  ecrireMemoire(CLE_REGISTRE, { version: VERSION_CACHE, eTag, lignes } satisfies MemoireRegistre)
  return { lignes, eTag, nouveau: true }
}

// ─── Registre inaccessible : ne pas réessayer de la journée ───

/** Le registre s'est montré inaccessible aujourd'hui : la recherche automatique se tait. */
export function inaccessibleAujourdhui(): boolean {
  return lireMemoire<string>(CLE_INACCESSIBLE) === jourLocal()
}

export function noterInaccessible(): void {
  ecrireMemoire(CLE_INACCESSIBLE, jourLocal())
}

// ─── La déclaration envoyée ───

/**
 * NVS, initiateur, dates… relus dans le formulaire déposé ; à défaut, dans son
 * nom. Gardé dans le navigateur : un fichier déposé ne change plus.
 */
export async function declarationEnvoyee(
  fichier: string,
  issueId: string,
  envoyeeLe: string,
  auteur: string,
): Promise<DeclarationEnvoyee> {
  const memoire = lireMemoire<Record<string, DeclarationEnvoyee>>(CLE_DECLARATIONS) ?? {}
  const connue = memoire[fichier]
  if (connue && connue.issueId === issueId) return connue
  let declaration: DeclarationEnvoyee
  try {
    const chemin = `${DOSSIER_NCS}/${fichier}`.split('/').map(encodeURIComponent).join('/')
    const element = await graphGet<ElementFichier>(`/sites/${SITE_ID}/drive/root:/${chemin}`)
    const url = element['@microsoft.graph.downloadUrl']
    if (!url) throw new Error('fichier NC-S sans adresse de téléchargement')
    const reponse = await fetch(url)
    if (!reponse.ok) throw new Error(`fichier NC-S (${reponse.status})`)
    declaration = await lireDeclarationEnvoyee(await reponse.arrayBuffer(), issueId, envoyeeLe)
  } catch (err) {
    console.warn('[registreNcs] formulaire déposé illisible, repli sur son nom :', err)
    return declarationDepuisNom(fichier, issueId, envoyeeLe, auteur)
  }
  // Les 50 dernières suffisent : au-delà, la référence est trouvée depuis longtemps.
  const entrees = Object.entries({ ...memoire, [fichier]: declaration }).slice(-50)
  ecrireMemoire(CLE_DECLARATIONS, Object.fromEntries(entrees))
  return declaration
}

// ─── Les déclarations envoyées, d'un coup ───

export interface DeclarationDeposee {
  /** Nom du fichier dans « Déclarations NC-S ». */
  fichier: string
  /** ID du problème : colonne Titre du fichier (écrite au dépôt). */
  issueId: string
  /** Date ISO du dépôt. */
  deposeeLe: string
  /** « Prénom Nom » de qui l'a déposée. */
  auteur: string
}

interface ElementDossier {
  name: string
  createdDateTime?: string
  createdBy?: { user?: { displayName?: string; email?: string } }
  listItem?: { fields?: { Title?: string } }
  file?: unknown
}

/**
 * Toutes les déclarations du dossier, avec le problème de chacune (colonne
 * Titre) — une requête pour toutes : la recherche de fond n'a pas à lire
 * l'activité de chaque problème. Les fichiers sans Titre (modèle vierge,
 * dépôt à la main) sont ignorés.
 */
export async function declarationsDeposees(): Promise<DeclarationDeposee[]> {
  const chemin = DOSSIER_NCS.split('/').map(encodeURIComponent).join('/')
  const resultat: DeclarationDeposee[] = []
  let page: string | undefined =
    `/sites/${SITE_ID}/drive/root:/${chemin}:/children` +
    '?$select=name,createdDateTime,createdBy,file&$expand=listItem($expand=fields($select=Title))&$top=999'
  while (page) {
    const r: { value: ElementDossier[]; '@odata.nextLink'?: string } = await graphGet(page)
    for (const e of r.value) {
      const issueId = e.listItem?.fields?.Title?.trim() ?? ''
      if (!e.file || !/^P20\d{2}-\d{4}$/.test(issueId)) continue
      resultat.push({
        fichier: e.name,
        issueId,
        deposeeLe: e.createdDateTime ?? '',
        auteur: nomComplet(e.createdBy?.user?.displayName ?? '', e.createdBy?.user?.email),
      })
    }
    page = r['@odata.nextLink']
  }
  return resultat
}

// ─── Un problème à la fois ───

const verrous = new Map<string, Promise<unknown>>()

/**
 * Une recherche à la fois par problème : la fiche ouverte et la recherche de
 * fond peuvent viser le même. La seconde attend la première, puis repart —
 * et trouve le N° déjà écrit (`enregistrerReferenceNcs` → `ecrit: false`).
 */
export async function avecVerrou<T>(issueId: string, action: () => Promise<T>): Promise<T> {
  const precedente = verrous.get(issueId) ?? Promise.resolve()
  const suivante = precedente.catch(() => undefined).then(action)
  verrous.set(issueId, suivante)
  try {
    return await suivante
  } finally {
    if (verrous.get(issueId) === suivante) verrous.delete(issueId)
  }
}

// ─── Le formulaire déposé, en entier (« Copier pour le registre », 06/10) ───

const formulaires = new Map<string, Promise<FormulaireDepose>>()

/** Le formulaire d'une NC-S déposée, relu dans « Déclarations NC-S » (gardé le temps de la session). */
export function formulaireDepose(fichier: string): Promise<FormulaireDepose> {
  let p = formulaires.get(fichier)
  if (!p) {
    p = (async () => {
      const chemin = `${DOSSIER_NCS}/${fichier}`.split('/').map(encodeURIComponent).join('/')
      const element = await graphGet<ElementFichier>(`/sites/${SITE_ID}/drive/root:/${chemin}`)
      const url = element['@microsoft.graph.downloadUrl']
      if (!url) throw new Error('fichier NC-S sans adresse de téléchargement')
      const reponse = await fetch(url)
      if (!reponse.ok) throw new Error(`fichier NC-S (${reponse.status})`)
      return lireFormulaireDepose(await reponse.arrayBuffer())
    })()
    p.catch(() => formulaires.delete(fichier))
    formulaires.set(fichier, p)
  }
  return p
}
